import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { AuthService } from '../services/auth';
import { Session, Teacher } from '../types';
import { ShareSessionModal } from '../components/ShareSessionModal';

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [lastLoadTime, setLastLoadTime] = useState<number>(0);
  const [shareModalSession, setShareModalSession] = useState<Session | null>(null);

  useEffect(() => {
    // Vérifier si un enseignant est connecté
    const currentTeacher = AuthService.getTeacher();
    if (!currentTeacher) {
      navigate('/teacher/login');
      return;
    }
    
    setTeacher(currentTeacher);
    // Charger les sessions directement avec l'enseignant
    loadSessionsWithTeacher(currentTeacher);
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessionsWithTeacher = async (teacherData: Teacher, forceReload = false) => {
    // Cache de 30 secondes pour éviter les requêtes inutiles
    const now = Date.now();
    if (!forceReload && lastLoadTime && (now - lastLoadTime) < 30000) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const startTime = Date.now();
      
      // Charger les sessions et les statistiques de l'enseignant en parallèle
      const [sessionsResponse, teacherResponse] = await Promise.all([
        ApiService.getTeacherSessions(teacherData.id),
        AuthService.getTeacherInfo(teacherData.id)
      ]);
      
      const loadTime = Date.now() - startTime;
      console.log(`Chargement des sessions et stats: ${loadTime}ms`);
      
      if (sessionsResponse.success) {
        setSessions(sessionsResponse.sessions);
        setLastLoadTime(now);
      } else {
        setError('Erreur lors du chargement des sessions');
      }
      
      if (teacherResponse.success) {
        setTeacher(teacherResponse.teacher);
        console.log('✅ Statistiques enseignant rechargées:', teacherResponse.teacher);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async (forceReload = false) => {
    if (!teacher) return;
    await loadSessionsWithTeacher(teacher, forceReload);
  };

  const createNewSession = async () => {
    if (!teacher) return;
    
    try {
      setError(null);
      
      const sessionName = `Session ${new Date().toLocaleDateString()}`;
      const response = await ApiService.createSession(sessionName, teacher.id);
      if (response.success) {
        // Recharger les informations de l'enseignant pour mettre à jour les stats
        try {
          const teacherResponse = await AuthService.getTeacherInfo(teacher.id);
          if (teacherResponse.success) {
            setTeacher(teacherResponse.teacher);
            console.log('✅ Statistiques enseignant mises à jour:', teacherResponse.teacher);
          }
        } catch (teacherError) {
          console.error('Erreur rechargement stats enseignant:', teacherError);
        }
        
        // Recharger les sessions pour voir la nouvelle session (force reload)
        loadSessions(true);
      } else {
        setError('Erreur lors de la création de la session');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création de la session');
    }
  };

  const handleLogout = () => {
    AuthService.logoutTeacher();
    navigate('/');
  };

  const deleteSession = async (sessionId: string, sessionName: string) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la session "${sessionName}" ?\n\nCette action supprimera également toutes les questions et réponses associées.`)) {
      return;
    }

    try {
      setError(null);
      const response = await ApiService.deleteSession(sessionId);
      
      if (response.success) {
        // Recharger les données de l'enseignant et des sessions
        if (teacher) {
          await loadSessionsWithTeacher(teacher, true);
        }
        console.log('✅ Session supprimée avec succès');
      } else {
        setError('Erreur lors de la suppression de la session');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression de la session');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateSessionStatus = async (sessionId: string, newStatus: string) => {
    try {
      setError(null);
      
      const response = await ApiService.updateSessionStatus(sessionId, newStatus);
      if (response.success) {
        // Recharger les sessions pour voir le changement (force reload)
        loadSessions(true);
      } else {
        setError('Erreur lors de la mise à jour de la session');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour de la session');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      created: 'status-created',
      active: 'status-active',
      paused: 'status-paused',
      ended: 'status-ended'
    };

    const statusLabels = {
      created: 'Créée',
      active: 'Active',
      paused: 'En pause',
      ended: 'Terminée'
    };

    return (
      <span className={`status-badge ${statusClasses[status as keyof typeof statusClasses]}`}>
        {statusLabels[status as keyof typeof statusLabels]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Tableau de bord</h1>
            <div className="header-actions">
              {teacher && (
                <div className="teacher-info">
                  <span className="teacher-name">
                    <i className="fas fa-user me-1"></i>
                    {teacher.name}
                  </span>
                  <span className="teacher-stats">
                    {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                  </span>
                </div>
              )}
              <button onClick={handleLogout} className="btn btn-outline btn-sm">
                <i className="fas fa-sign-out-alt me-1"></i>
                Déconnexion
              </button>
              <Link to="/" className="btn btn-outline btn-sm">
                ← Accueil
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">Tableau de bord</h1>
          <div className="header-actions">
            {teacher && (
              <div className="teacher-info">
                <span className="teacher-name">
                  <i className="fas fa-user me-1"></i>
                  {teacher.name}
                </span>
                <span className="teacher-stats">
                  {teacher.totalSessions} sessions • {teacher.totalStudents} étudiants
                </span>
              </div>
            )}
            <button onClick={handleLogout} className="btn btn-outline btn-sm">
              <i className="fas fa-sign-out-alt me-1"></i>
              Déconnexion
            </button>
            <Link to="/" className="btn btn-outline btn-sm">
              ← Accueil
            </Link>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Mes Sessions</h2>
            <div className="flex gap-2">
              <button 
                className="btn btn-outline btn-sm" 
                onClick={() => loadSessions(true)}
                disabled={loading}
              >
                🔄 Actualiser
              </button>
              <button className="btn btn-primary" onClick={createNewSession}>
                ➕ Nouvelle Session
              </button>
            </div>
          </div>
          
          {error && (
            <div className="mb-3 p-2" style={{ background: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
              {error}
            </div>
          )}

          {sessions.length === 0 ? (
            <div className="text-center p-4">
              <p className="mb-3">Aucune session créée pour le moment.</p>
              <button className="btn btn-primary" onClick={createNewSession}>
                Créer ma première session
              </button>
            </div>
          ) : (
            <div className="grid grid-2">
              {sessions.map((session) => (
                <div key={session.id} className="card">
                  <div className="card-header">
                    <h3 className="card-title">{session.name}</h3>
                    {getStatusBadge(session.status)}
                  </div>
                  <div className="card-content">
                    <div className="mb-2">
                      <strong>Code:</strong> <code>{session.code}</code>
                    </div>
                    <div className="mb-2">
                      <strong>Participants:</strong> {session.participantsCount}
                    </div>
                    <div className="mb-2">
                      <strong>Questions:</strong> {session.questionsCount || 0}
                    </div>
                    <div className="mb-2">
                      <strong>Créée le:</strong> {new Date(session.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="card-actions">
                    <Link 
                      to={`/teacher/sessions/${session.id}`} 
                      className="btn btn-primary btn-sm"
                    >
                      Gérer
                    </Link>
                    {session.status === 'created' && (
                      <button
                        onClick={() => updateSessionStatus(session.id, 'active')}
                        className="btn btn-success btn-sm"
                      >
                        Démarrer
                      </button>
                    )}
                    {session.status === 'active' && (
                      <>
                        <Link 
                          to={`/teacher/sessions/${session.id}/live`} 
                          className="btn btn-secondary btn-sm"
                        >
                          Session Live
                        </Link>
                        <button
                          onClick={() => updateSessionStatus(session.id, 'ended')}
                          className="btn btn-danger btn-sm"
                        >
                          Arrêter
                        </button>
                      </>
                    )}
                    {session.status === 'ended' && (
                      <span className="text-muted">Session terminée</span>
                    )}
                    {(session.status === 'ended' || session.status === 'created') && (
                      <>
                        {/* Afficher le bouton de partage pour les sessions dont l'utilisateur est propriétaire */}
                        {!session.isShared && (
                          <button
                            onClick={() => setShareModalSession(session)}
                            className="btn btn-secondary btn-sm"
                            style={{ marginLeft: '0.5rem' }}
                            title="Partager cette session avec un autre enseignant"
                          >
                            🔗 Partager{session.sharedWith && session.sharedWith.length > 0 ? ` (${session.sharedWith.length})` : ''}
                          </button>
                        )}
                        {/* Pour les sessions partagées avec l'utilisateur (isShared = true), afficher un badge différent */}
                        {session.isShared && (
                          <span className="badge" style={{ marginLeft: '0.5rem', backgroundColor: '#6c757d', color: 'white' }}>
                            Reçue
                          </span>
                        )}
                        <button
                          onClick={() => deleteSession(session.id, session.name)}
                          className="btn btn-danger btn-sm"
                          style={{ marginLeft: '0.5rem' }}
                        >
                          🗑️ Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📊 Statistiques Rapides</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-3">
              <div className="text-center">
                <h4>{sessions.length}</h4>
                <p>Sessions créées</p>
              </div>
              <div className="text-center">
                <h4>{sessions.filter(s => s.status === 'active').length}</h4>
                <p>Sessions actives</p>
              </div>
              <div className="text-center">
                <h4>{sessions.reduce((acc, s) => acc + s.participantsCount, 0)}</h4>
                <p>Total participants</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal de partage */}
      {shareModalSession && (
        <ShareSessionModal
          sessionId={shareModalSession.id}
          sessionName={shareModalSession.name}
          currentSharedWith={shareModalSession.sharedWith}
          onClose={() => setShareModalSession(null)}
          onUpdate={() => {
            loadSessions(true);
            setShareModalSession(null);
          }}
        />
      )}
    </div>
  );
};

export default TeacherDashboard;
